import { useState, useEffect } from 'react';
import { Clock, Play, Loader as Loader2, CircleCheck as CheckCircle2, Circle as XCircle, ChevronDown, ChevronUp, Zap } from 'lucide-react';
import type { SkillExecution, StepLog } from './types';
import { supabase } from '@/lib/supabase';
import { useCompany } from '@/context/CompanyContext';

export function SkillExecutionHistory() {
  const { activeCompanyId } = useCompany();
  const [executions, setExecutions] = useState<SkillExecution[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!activeCompanyId) return;
    setLoading(true);
    supabase
      .from('skill_executions')
      .select(`
        *,
        company_skills(name_override, skill_definitions(name_he)),
        workflow_definitions(name)
      `)
      .eq('company_id', activeCompanyId)
      .order('started_at', { ascending: false })
      .limit(50)
      .then(({ data }) => {
        setExecutions((data as SkillExecution[]) ?? []);
        setLoading(false);
      });
  }, [activeCompanyId]);

  const getName = (ex: SkillExecution) => {
    if (ex.workflow_definitions) return ex.workflow_definitions.name;
    if (ex.company_skills) {
      return ex.company_skills.name_override ?? ex.company_skills.skill_definitions?.name_he ?? 'סקיל';
    }
    return 'הרצה';
  };

  const totalSaved = executions
    .filter(e => e.status === 'success')
    .reduce((acc, e) => acc + (e.duration_ms ?? 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" dir="rtl">

      {/* Stats */}
      {executions.length > 0 && (
        <div className="grid grid-cols-3 gap-4">
          <StatCard label="סה״כ הרצות" value={String(executions.length)} color="blue" />
          <StatCard label="הצלחות" value={String(executions.filter(e => e.status === 'success').length)} color="emerald" />
          <StatCard label="זמן עיבוד" value={`${Math.round(totalSaved / 1000)}ש'`} color="amber" />
        </div>
      )}

      {/* List */}
      {executions.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <Play className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm">עדיין לא בוצעו הרצות. הפעל סקיל כדי להתחיל.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {executions.map(ex => (
            <ExecutionRow
              key={ex.id}
              execution={ex}
              name={getName(ex)}
              isExpanded={!!expanded[ex.id]}
              onToggle={() => setExpanded(prev => ({ ...prev, [ex.id]: !prev[ex.id] }))}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, color }: { label: string; value: string; color: 'blue' | 'emerald' | 'amber' }) {
  const cls = {
    blue: 'bg-blue-50 text-blue-700',
    emerald: 'bg-emerald-50 text-emerald-700',
    amber: 'bg-amber-50 text-amber-700',
  }[color];
  return (
    <div className={`rounded-2xl p-4 ${cls}`}>
      <p className="text-xs font-medium opacity-75">{label}</p>
      <p className="text-2xl font-bold mt-1">{value}</p>
    </div>
  );
}

function ExecutionRow({ execution: ex, name, isExpanded, onToggle }: {
  execution: SkillExecution;
  name: string;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const statusIcon = ex.status === 'success'
    ? <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
    : ex.status === 'error'
      ? <XCircle className="w-4 h-4 text-red-500 shrink-0" />
      : ex.status === 'running'
        ? <Loader2 className="w-4 h-4 text-blue-500 animate-spin shrink-0" />
        : <Clock className="w-4 h-4 text-gray-400 shrink-0" />;

  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
      <div
        className="px-5 py-4 flex items-center gap-3 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={onToggle}
      >
        {statusIcon}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900">{name}</p>
          {ex.output_summary && (
            <p className="text-xs text-gray-500 mt-0.5 truncate">{ex.output_summary}</p>
          )}
        </div>
        <div className="text-xs text-gray-400 shrink-0 flex items-center gap-2">
          <Clock className="w-3 h-3" />
          {new Date(ex.started_at).toLocaleString('he-IL', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
          {ex.duration_ms && <span className="bg-gray-100 px-1.5 py-0.5 rounded text-gray-500">{ex.duration_ms < 1000 ? `${ex.duration_ms}ms` : `${(ex.duration_ms / 1000).toFixed(1)}s`}</span>}
          {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        </div>
      </div>

      {isExpanded && (
        <div className="border-t border-gray-100 px-5 py-4 space-y-3">
          {ex.steps_log.length === 0 && <p className="text-sm text-gray-400">אין פרטי שלבים</p>}
          {ex.steps_log.map((log, i) => (
            <StepLogRow key={i} log={log} index={i} />
          ))}
          {ex.error_message && (
            <div className="p-3 bg-red-50 rounded-xl text-sm text-red-700">{ex.error_message}</div>
          )}
        </div>
      )}
    </div>
  );
}

function StepLogRow({ log, index }: { log: StepLog; index: number }) {
  const [showData, setShowData] = useState(false);
  const isFileAnalysis = log.output?.type === 'file_analysis';
  return (
    <div className={`p-3 rounded-xl text-sm ${log.status === 'error' ? 'bg-red-50' : 'bg-gray-50'}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className={`w-5 h-5 rounded-full text-xs font-bold flex items-center justify-center shrink-0 ${log.status === 'success' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
            {index + 1}
          </span>
          <span className="font-medium text-gray-800">{log.step_name}</span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-xs text-gray-400">{log.duration_ms < 1000 ? `${log.duration_ms}ms` : `${(log.duration_ms / 1000).toFixed(1)}s`}</span>
          {log.output?.row_count !== undefined && (
            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">{log.output.row_count} שורות</span>
          )}
          {log.output && (
            <button onClick={() => setShowData(!showData)} className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700">
              <Zap className="w-3 h-3" />
              {showData ? 'הסתר' : 'הצג'}
            </button>
          )}
        </div>
      </div>
      {log.error && <p className="text-xs text-red-600 mt-2 mr-7">{log.error}</p>}
      {showData && isFileAnalysis && log.output?.analysis && (
        <div className="mt-3 p-4 bg-white rounded-xl border border-gray-200 text-sm text-gray-800 whitespace-pre-wrap leading-relaxed max-h-96 overflow-y-auto" dir="rtl">
          {log.output.analysis}
        </div>
      )}
      {showData && !isFileAnalysis && log.output?.rows && log.output.rows.length > 0 && (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="bg-gray-100">
                {Object.keys(log.output.rows[0]!).map(col => (
                  <th key={col} className="px-2 py-1.5 text-right font-semibold text-gray-600 whitespace-nowrap border border-gray-200">{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {log.output.rows.slice(0, 20).map((row, ri) => (
                <tr key={ri} className="even:bg-gray-50">
                  {Object.values(row).map((val, vi) => (
                    <td key={vi} className="px-2 py-1.5 text-gray-700 whitespace-nowrap border border-gray-200">{val === null ? '—' : String(val)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {log.output.rows.length > 20 && <p className="text-xs text-gray-400 mt-1">מוצגות 20 מתוך {log.output.rows.length}</p>}
        </div>
      )}
    </div>
  );
}
